// TimerV2Setups.jsx — Live TIMER v2 coil-setup panel (Sep 23 2026)
//
// WHY THIS EXISTS
// PipelineScores reads pipeline:score:* which is only written inside
// _run_pipeline_inner — i.e. ONLY when a trigger actually fires. TIMER v2 reaches
// that function on a fire and never otherwise, so a healthy engine tracking three
// coiled setups looked identical to a dead one: an empty panel. Diagnosing the
// Sep 20–23 silence required tailing journalctl.
//
// This panel reads /api/timer-v2-setups (Redis timer_v2:arm:*), which carries the
// HOLD states too, plus the five-layer breakdown and the single binding constraint.
//
// SCALE WARNING: the score here is TIMER v2's own 0–10 setup-quality score, NOT the
// legacy 12-pt pipeline score shown in PipelineScores. They are different scales and
// move in OPPOSITE directions — this one is highest while price is still coiled and
// FALLS as price expands away from the base. Do not compare the two numbers.
import React from "react";

// Mirrors backend config: timer_v2_min_score (fire floor, 0–10 scale).
const MIN_SCORE = 7.0;

// Layer maxima from signals/timer_v2.py (_MAX_* constants).
const LAYER_MAX = { trend: 3.0, energy: 3.0, positioning: 3.0, room: 2.0, timing: 2.0 };
const LAYER_ORDER = ["trend", "energy", "positioning", "room", "timing"];

// Human text for decide()'s veto_reason values. A veto short-circuits BEFORE the
// score is compared, so a vetoed setup can show a high score and still not fire —
// that distinction is the whole point of surfacing blocked_by separately.
const BLOCKED_LABELS = {
  score:             "Score below floor",
  score_below_floor: "Score below floor",
  timing_veto:       "Timing window",
  positioning_veto:  "Options positioning",
  room_veto:         "No room to run",
  energy_veto:       "Energy too low",
};

const BLOCKED_COLORS = {
  score:             "bg-slate-100 text-slate-600",
  score_below_floor: "bg-slate-100 text-slate-600",
  timing_veto:       "bg-amber-100 text-amber-700",
  positioning_veto:  "bg-orange-100 text-orange-700",
  room_veto:         "bg-rose-100 text-rose-700",
  energy_veto:       "bg-purple-100 text-purple-700",
};

function scoreColor(score) {
  if (score >= MIN_SCORE)       return "text-green-700 font-bold";
  if (score >= MIN_SCORE - 1.0) return "text-yellow-700 font-semibold";
  return "text-gray-400";
}

function LayerBar({ name, value }) {
  const max = LAYER_MAX[name] || 1;
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const tone =
    pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-yellow-500" : "bg-gray-300";
  return (
    <div className="flex items-center gap-1" title={`${name} ${value.toFixed(2)}/${max.toFixed(1)}`}>
      <span className="text-[10px] text-gray-500 w-14 capitalize">{name}</span>
      <div className="w-12 h-1.5 bg-gray-200 rounded overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-gray-600 tabular-nums w-7">{value.toFixed(1)}</span>
    </div>
  );
}

export default function TimerV2Setups({ setups = [], hold = 0, fired = 0 }) {
  if (!setups.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-700">Timer v2 — Live Coil Setups</h3>
          <span className="text-[11px] text-gray-400">0 tracked</span>
        </div>
        <p className="text-xs text-gray-400">
          No symbol is currently past Stage&nbsp;A. This is a normal waiting state, not an
          error — Timer&nbsp;v2 only tracks symbols with an established trend plus an active
          volatility coil.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mt-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-700">Timer v2 — Live Coil Setups</h3>
        <span className="text-[11px] text-gray-500">
          {hold} hold · {fired} fired · floor {MIN_SCORE.toFixed(1)}/10
        </span>
      </div>
      <p className="text-[11px] text-gray-400 mb-3">
        Setup-quality score on Timer v2&apos;s own 0–10 scale — <strong>not</strong> the 12-pt
        pipeline score below. It is highest while price is still coiled and falls as price
        expands, so a dropping score means the entry is getting later, not worse-quality.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase text-gray-400 border-b border-gray-200">
              <th className="text-left  font-medium py-1.5 pr-2">Symbol</th>
              <th className="text-left  font-medium py-1.5 pr-2">Dir</th>
              <th className="text-right font-medium py-1.5 pr-2">Score</th>
              <th className="text-left  font-medium py-1.5 pr-2">Layers</th>
              <th className="text-left  font-medium py-1.5 pr-2">Blocked by</th>
              <th className="text-right font-medium py-1.5 pr-2">Room</th>
              <th className="text-right font-medium py-1.5 pr-2">Coil</th>
              <th className="text-right font-medium py-1.5">Age</th>
            </tr>
          </thead>
          <tbody>
            {setups.map((s) => {
              const layers = s.layers || {};
              const isFired = s.state === "FIRED";
              const blocked = s.blocked_by || "";
              return (
                <tr
                  key={s.symbol}
                  className={`border-b border-gray-100 ${
                    isFired ? "border-l-2 border-l-green-400 bg-green-50/40" : ""
                  }`}
                >
                  <td className="py-2 pr-2 font-medium text-gray-800">{s.symbol}</td>
                  <td className="py-2 pr-2">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        s.direction === "BULLISH"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {s.direction === "BULLISH" ? "LONG" : "SHORT"}
                    </span>
                  </td>
                  <td className={`py-2 pr-2 text-right tabular-nums ${scoreColor(s.score || 0)}`}>
                    {(s.score || 0).toFixed(2)}
                    <span className="text-gray-300 font-normal">/10</span>
                  </td>
                  <td className="py-2 pr-2">
                    <div className="grid grid-cols-1 gap-0.5">
                      {LAYER_ORDER.filter((k) => layers[k] !== undefined).map((k) => (
                        <LayerBar key={k} name={k} value={Number(layers[k]) || 0} />
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-2">
                    {isFired ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                        FIRED
                      </span>
                    ) : (
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          BLOCKED_COLORS[blocked] || "bg-gray-100 text-gray-600"
                        }`}
                        title={blocked}
                      >
                        {BLOCKED_LABELS[blocked] || blocked || "—"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-gray-600">
                    {s.room_pct !== undefined ? `${Number(s.room_pct).toFixed(2)}%` : "—"}
                    {s.obstacle_kind ? (
                      <div className="text-[9px] text-gray-400">{s.obstacle_kind}</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-2 text-right tabular-nums text-gray-600">
                    {s.squeeze_bars !== undefined ? `${s.squeeze_bars}b` : "—"}
                    {s.atr_ratio !== undefined ? (
                      <div className="text-[9px] text-gray-400">
                        ATR {Number(s.atr_ratio).toFixed(2)}x
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 text-right tabular-nums text-gray-400 text-[10px]">
                    {s.age_seconds !== null && s.age_seconds !== undefined
                      ? `${Math.round(s.age_seconds)}s`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
